import React, { useState, useEffect } from 'react';
import { Lightbulb, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { saveIdea, checkIdeaSaved, deleteIdea } from '../utils/api';

const SaveToIdeasButton = ({ 
  title, 
  category, 
  sourcePage, 
  sourceUrl,
  variant = 'default' // 'default' | 'compact'
}) => {
  const [status, setStatus] = useState('checking'); // checking | idle | loading | saved | removing
  const [savedIdeaId, setSavedIdeaId] = useState(null);

  // Check if already saved on mount
  useEffect(() => {
    const checkSaved = async () => {
      try {
        const result = await checkIdeaSaved(sourceUrl);
        if (result.saved) {
          setStatus('saved');
          setSavedIdeaId(result.idea_id);
        } else {
          setStatus('idle');
        }
      } catch (error) {
        console.error('Error checking idea status:', error);
        setStatus('idle');
      }
    };
    checkSaved();
  }, [sourceUrl]);

  const handleClick = async () => {
    if (status === 'loading' || status === 'removing' || status === 'checking') return;

    if (status === 'saved' && savedIdeaId) {
      // Remove the idea
      setStatus('removing');
      try {
        await deleteIdea(savedIdeaId);
        setStatus('idle');
        setSavedIdeaId(null);
        toast.success('Removed from My Ideas');
      } catch (error) {
        console.error('Error removing idea:', error);
        setStatus('saved');
        toast.error('Failed to remove');
      }
    } else {
      // Save the idea
      setStatus('loading');
      try {
        const response = await saveIdea({
          title,
          category,
          source_page: sourcePage,
          source_url: sourceUrl
        });
        setStatus('saved');
        setSavedIdeaId(response.id);
        toast.success('Added to My Ideas');
      } catch (error) {
        if (error.response?.status === 400) {
          setStatus('saved');
        } else {
          setStatus('idle');
          toast.error('Failed to save');
        }
      }
    }
  };

  const isSaved = status === 'saved';
  const isLoading = status === 'loading';
  const isRemoving = status === 'removing';
  const isChecking = status === 'checking';

  // Don't render anything while checking initial state
  if (isChecking) {
    return null;
  }

  if (variant === 'compact') {
    return (
      <button
        onClick={handleClick}
        disabled={isLoading || isRemoving}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-full transition-all ${
          isSaved
            ? 'bg-green-100 text-green-700 hover:bg-red-50 hover:text-red-600'
            : isLoading || isRemoving
            ? 'bg-slate-100 text-slate-400 cursor-wait'
            : 'bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200'
        }`}
        title={isSaved ? 'Click to remove' : 'Save to My Ideas'}
      >
        {isLoading || isRemoving ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : isSaved ? (
          <Check className="h-3.5 w-3.5" />
        ) : (
          <Lightbulb className="h-3.5 w-3.5" />
        )}
        <span>{isRemoving ? 'Removing...' : isSaved ? 'Saved' : 'Save to My Ideas'}</span>
      </button>
    );
  }

  // Default variant - more prominent button
  return (
    <button
      onClick={handleClick}
      disabled={isLoading || isRemoving}
      className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl transition-all font-medium ${
        isSaved
          ? 'bg-green-100 text-green-700 hover:bg-red-50 hover:text-red-600 border border-green-200 hover:border-red-200'
          : isLoading || isRemoving
          ? 'bg-slate-100 text-slate-400 cursor-wait'
          : 'bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200 hover:border-amber-300'
      }`}
      title={isSaved ? 'Click to remove' : 'Save to My Ideas'}
    >
      {isLoading || isRemoving ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : isSaved ? (
        <Check className="h-4 w-4" />
      ) : (
        <Lightbulb className="h-4 w-4" />
      )}
      <span>{isRemoving ? 'Removing...' : isSaved ? 'Saved' : 'Save to My Ideas'}</span>
    </button>
  );
};

export default SaveToIdeasButton;
